"use client";

import type { ChangeEvent, ComponentType, SVGProps } from "react";
import type { LeadFlowChoiceArrangement } from "../../types";

import { cn } from "@homeinstead/ui";
import { buttonVariants } from "@homeinstead/ui/components/Button";
import Image from "next/image";
import { getButtonVariant } from "../../utils/get-button-variant";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";
import { CheckBadge } from "./CheckBadge";
import { MultiSelectIndicator } from "./MultiSelectIndicator";

const ARRANGEMENT_CLASS: Record<LeadFlowChoiceArrangement, string> = {
  cards: "min-h-12 items-start rounded-lg px-5 py-4 text-left",
  list: "h-14 min-h-14 items-center rounded-full px-6 py-0 text-left text-md leading-9",
  "tile-grid":
    "min-h-28 flex-col items-center justify-center rounded-lg px-4 py-4 text-center",
};

/**
 * Single-choice pills keep one look whatever the step theme: white
 * with a New Beginnings outline, filling Clear Sky on hover and when picked.
 */
const SINGLE_CHOICE_CLASS =
  "border-2 border-new-beginnings-base bg-white font-bold text-new-beginnings-base hover:border-clear-sky-base hover:bg-clear-sky-base hover:shadow-primary-button";

const SINGLE_CHOICE_SELECTED_CLASS =
  "border-clear-sky-base bg-clear-sky-base pr-14 shadow-primary-button hover:bg-clear-sky-tints-500";

/**
 * On a Calm step the pills take the outline button's hover instead,
 * since a Clear Sky fill would vanish into the surface: the New Beginnings
 * outline stays and the pill fills with its tint. A picked pill holds the
 * hover look, like a picked takeover card.
 */
const CALM_SINGLE_CHOICE_HOVER_CLASS =
  "hover:border-new-beginnings-base hover:bg-new-beginnings-tints-800";

const CALM_SINGLE_CHOICE_SELECTED_CLASS =
  "bg-new-beginnings-tints-800 pr-14 shadow-primary-button";

/** In the takeover, pills are shorter below `md`. */
const TAKEOVER_SINGLE_CHOICE_CLASS =
  "max-md:h-12 max-md:min-h-12 max-md:text-base max-md:leading-6";

/**
 * Multiselect checkbox cards are white with a transparent border, so hover
 * and selection do not shift layout, filling New Beginnings tint when hovered
 * or picked.
 */
const CHECKBOX_CARD_CLASS =
  "justify-between gap-3 rounded-sm border-2 border-transparent bg-white p-4 text-left text-new-beginnings-base hover:border-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:shadow-primary-button";

const CHECKBOX_CARD_SELECTED_CLASS =
  "border-new-beginnings-base bg-new-beginnings-tints-800";

type ChoiceOptionProps = {
  arrangement: LeadFlowChoiceArrangement;
  /** Shown when the answer has no authored icon. */
  builtInIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  description?: string | null;
  iconUrl?: string | null;
  isSelected: boolean;
  label: string;
  name: string;
  /** Fires on every pick, including a pick of the already-selected answer. */
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  type: "checkbox" | "radio";
  value: string;
};

/**
 * One answer. Single-choice answers are pill buttons with a check badge on
 * the selected one; multi-select answers are checkbox cards. The input stays
 * a native radio or checkbox for keyboard and screen reader behavior.
 */
function ChoiceOption({
  arrangement,
  builtInIcon: BuiltInIcon,
  description,
  iconUrl,
  isSelected,
  label,
  name,
  onSelect,
  type,
  value,
}: ChoiceOptionProps) {
  const {
    isSectionTakeover = false,
    questionStyle,
    stepTheme,
    themeContrast,
  } = useLeadFlowRuntime();
  const variant = getButtonVariant(themeContrast);
  const isSingleChoice = type === "radio";
  const isCheckboxCard =
    type === "checkbox" && questionStyle.multiSelectFormat === "checkbox-card";
  const isTakeoverPill = isSectionTakeover && isSingleChoice;
  const isCalmPill = isSingleChoice && stepTheme === "calm";

  return (
    <label
      className={cn(
        isCheckboxCard
          ? undefined
          : isSingleChoice
            ? buttonVariants({ variant: "plain" })
            : buttonVariants({ variant }),
        "group relative flex h-auto w-full cursor-pointer justify-start gap-4 whitespace-normal font-semibold",
        "focus-within:outline focus-within:outline-2 focus-within:outline-new-beginnings-base focus-within:outline-offset-2",
        isCheckboxCard ? CHECKBOX_CARD_CLASS : ARRANGEMENT_CLASS[arrangement],
        isCheckboxCard &&
          (description ? "h-[104px] items-start" : "h-[72px] items-center"),
        isSingleChoice && SINGLE_CHOICE_CLASS,
        isCalmPill && CALM_SINGLE_CHOICE_HOVER_CLASS,
        isTakeoverPill && TAKEOVER_SINGLE_CHOICE_CLASS,
        isSingleChoice &&
          isSelected &&
          (isCalmPill
            ? CALM_SINGLE_CHOICE_SELECTED_CLASS
            : SINGLE_CHOICE_SELECTED_CLASS),
        isCheckboxCard && isSelected && CHECKBOX_CARD_SELECTED_CLASS,
        !isSingleChoice &&
          !isCheckboxCard &&
          variant === "outline" &&
          isSelected &&
          "bg-new-beginnings-tints-800",
      )}
      data-selected={isSelected || undefined}
    >
      <input
        checked={isSelected}
        className="sr-only"
        name={name}
        onChange={onSelect}
        // A click on the selected radio fires no change event, so re-picking
        // a restored answer would not auto-advance without this. The handler
        // only reads the input, which this click already targets.
        onClick={(event) => {
          if (type === "radio" && isSelected) {
            onSelect(event as unknown as ChangeEvent<HTMLInputElement>);
          }
        }}
        type={type}
        value={value}
      />
      {iconUrl ? (
        <Image
          alt=""
          className={cn(
            "shrink-0",
            isCheckboxCard ? "mt-0.5 size-5" : "size-8",
          )}
          height={isCheckboxCard ? 20 : 32}
          src={iconUrl}
          width={isCheckboxCard ? 20 : 32}
        />
      ) : BuiltInIcon ? (
        // Glyphs keep their own proportions; wider ones overhang the slot
        // evenly so every label starts at the same inset.
        <span className="flex size-8 shrink-0 items-center justify-center">
          <BuiltInIcon
            aria-hidden="true"
            className="h-8 w-auto shrink-0 fill-new-beginnings-base"
          />
        </span>
      ) : null}
      {isCheckboxCard ? (
        <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <span className="font-bold text-md">{label}</span>
          {description ? (
            <span className="font-normal text-default">{description}</span>
          ) : null}
        </span>
      ) : (
        <span className="flex flex-1 flex-col gap-1">
          <span>{label}</span>
          {description ? (
            <span className="font-normal text-sm">{description}</span>
          ) : null}
        </span>
      )}
      {type === "radio" && isSelected ? (
        <CheckBadge
          className={isCalmPill ? "bg-new-beginnings-base" : undefined}
        />
      ) : null}
      {isCheckboxCard ? (
        <MultiSelectIndicator isSelected={isSelected} />
      ) : type === "checkbox" ? (
        <span
          aria-hidden
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-sm border-2 border-current",
            isSelected && "bg-current",
          )}
          data-testid="lead-flow-checkbox-indicator"
        />
      ) : null}
    </label>
  );
}

export { ChoiceOption };
